import { DataGrid, GridCellEditStopParams, GridColDef, getGridStringOperators } from "@mui/x-data-grid";
import {
  GENDER_OPTIONS,
  RACE_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  EDUCATION_LEVEL_OPTIONS,
  DEPENDENT_TYPE_OPTIONS,
} from "v2/constants/esocial-options";
import { IMassaTotal } from './AppEditData.types'
import { cpfMask } from "utils/cpf";
import { formatCnpj } from "utils/federal-tax-id";
import { ignoreFederalTaxIdMask } from "utils/federal-tax-id-grid";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { Box, Button, CircularProgress, IconButton, Modal, Table, TableCell, TableContainer, TableRow } from "@mui/material";
import { ArrowBack, Close } from "@mui/icons-material";
import api from "services/api";
import { useLocation, useParams } from "react-router-dom";
import { ptBR } from '@mui/x-data-grid/locales';
import { useAlert } from "contexts/alert";
import { useQueryClient } from "@tanstack/react-query";
import { NameWithBadge } from "./AppEditData.regime";
import { aplicarParticao, buildEditDataPayload, parseDateOnly } from "./AppEditData.payload";
import { initialCpfFilter } from "./AppEditData.utils";

/** Filtro da coluna CNPJ que ignora a máscara (ver `utils/federal-tax-id-grid`). */
const cnpjFilterOperators = ignoreFederalTaxIdMask(getGridStringOperators());

type Props = {
  people: IMassaTotal[];
  editableFields: string[];
  setCurrentView?: (payload: 'ADDING' | 'EDITING' | 'DEFAULT') => void;
  /** Atividade de Editar dados; prevalece sobre a da rota (usado quando a tabela vive em outra tela). */
  workspaceActivityId?: string;
  /** Por CPF sem pontuação: a geração e o tipo de movimentação que cada item do payload carrega. */
  partição?: Record<string, { dagRunId: string; movementType?: string }>;
  /** Quando definido, recebe o resultado do save no lugar do aviso padrão. */
  onSalvo?: (resultado: { pessoas: { taxPayerId: string; linhasNaMassa: number | null }[] }) => void;
}

/**
 * Mapeia a resposta da API para as linhas da grid.
 *
 * Extraído de propósito (mesmo padrão de AppInclusion.Table.tsx): usado na
 * montagem inicial, na ressincronia quando `people` muda e em "Descartar
 * alterações", para que as três cópias nunca divirjam entre si.
 */
const buildRows = (people: IMassaTotal[]) => people.map((row, index) => ({
  id: row?.ID ?? `${index + 1}_${Date.now()}`,

  // Common fields
  name: row?.NAME ?? '',
  taxPayerId: !!row?.TAX_PAYER_ID ? cpfMask(row.TAX_PAYER_ID) : '',
  taxPayerIdEmployee: !!row?.EMPLOYEE_TAX_PAYER_ID ? cpfMask(row.EMPLOYEE_TAX_PAYER_ID) : '',
  birthDate: !!row?.BIRTH_DATE ? new Date(row.BIRTH_DATE) : null,
  motherName: row?.MOTHER_NAME ? row.MOTHER_NAME : '',
  weddingDate: !!row?.WEDDING_DATE ? new Date(row.WEDDING_DATE) : null,
  type: row?.PERSON_TYPE ?? '',

  // Employee specific fields
  organizationId: row?.ORGANIZATION_ID ?? '',
  cnpj: row?.LEGAL_ENTITY_TAX_ID ?? '',
  pis: row?.PIS ?? '',
  genderId: row?.GENDER_ID ?? '',
  raceId: row?.RACE_ID ?? '',
  maritalStatusId: row?.MARITAL_STATUS_ID ?? '',
  educationLevelId: row?.EDUCATION_LEVEL_ID ?? '',
  socialName: row?.SOCIAL_NAME ?? '',
  birthCountryId: row?.BIRTH_COUNTRY_ID ?? '',
  nationalityCountryId: row?.NATIONALITY_COUNTRY_ID ?? '',
  birthStateId: row?.BIRTH_STATE_ID ?? '',
  birthCityId: row?.BIRTH_CITY_ID ?? '',
  fatherName: row?.FATHER_NAME ?? '',
  zipCode: row?.ZIP_CODE ?? '',
  addressType: row?.ADDRESS_TYPE ?? '',
  address: row?.ADDRESS ?? '',
  addressNumber: row?.ADDRESS_NUMBER ?? '',
  complement: row?.COMPLEMENT ?? '',
  neighborhood: row?.NEIGHBORHOOD ?? '',
  stateId: row?.STATE_ID ?? '',
  cityId: row?.CITY_ID ?? '',
  nationalIdentityNumber: row?.NATIONAL_IDENTITY_NUMBER ?? '',
  nationalIdentityEmissor: row?.NATIONAL_IDENTITY_EMISSOR ?? '',
  nationalIdentityExpeditionDate: !!row?.NATIONAL_IDENTITY_EXPEDITION_DATE ? new Date(row.NATIONAL_IDENTITY_EXPEDITION_DATE) : null,
  phoneNumber: row?.PHONE_NUMBER ?? '',
  email: row?.EMAIL ?? '',
  hiringDate: !!row?.HIRING_DATE ? new Date(row.HIRING_DATE) : null,

  // New fields from IMassaTotal
  status: row?.STATUS ?? '',
  jobTitle: row?.JOB_TITLE ?? '',
  paymentFrequency: row?.PAYMENT_FREQUENCY ?? '',
  grossPayment: row?.GROSS_PAYMENT ?? '',
  municipality: row?.MUNICIPALITY ?? '',
  cbo: row?.CBO ?? '',
  dismissalDate: parseDateOnly(row?.DISMISSAL_DATE),
  startPriorNoticeDate: !!row?.START_PRIOR_NOTICE_DATE ? new Date(row.START_PRIOR_NOTICE_DATE) : null,
  endPriorNoticeDate: !!row?.END_PRIOR_NOTICE_DATE ? new Date(row.END_PRIOR_NOTICE_DATE) : null,
  transferred: row?.TRANSFERRED ?? '',
  plan: row?.PLAN ?? '',
  maintainedByPj: !!row?.MAINTAINED_BY_PJ,

  // Dependent specific fields
  employeeId: row?.EMPLOYEE_ID ?? '',
  dependentTypeId: row?.DEPENDENT_TYPE_ID ?? '',
  isDeleted: row?.IS_DELETED !== null ? (row.IS_DELETED ? 'Sim' : 'Não') : '-',
  isStudent: row?.IS_STUDENT !== null ? (row.IS_STUDENT ? 'Sim' : 'Não') : '-',

  // Timestamps
  createdAt: !!row?.CREATED_AT ? dayjs(row.CREATED_AT).format("DD/MM/YYYY HH:mm") : '-',
  updatedAt: !!row?.UPDATED_AT ? dayjs(row.UPDATED_AT).format("DD/MM/YYYY HH:mm") : '-',
}));

export function AppEditDataTable({ people, editableFields, setCurrentView, workspaceActivityId: waIdProp, partição, onSalvo }: Props) {
  const { workspaceId, workspaceActivityId: waIdRota } = useParams() as { workspaceId: string, workspaceActivityId: string };
  const workspaceActivityId = waIdProp ?? waIdRota;
  const { search } = useLocation();
  const { openAlert } = useAlert();
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSendingUpdate, setIsSendingUpdate] = useState(false);

  const [realRows, setRealRows] = useState(() => buildRows(people));

  const [changes, setChanges] = useState<null | Array<{
    [key: string]: any;
  }>>(null);

  /**
   * Ressincroniza a grid quando o polling do AppEditData.View.tsx (5s) devolve
   * dados novos. Sem isto a grid ficava congelada no snapshot da abertura: ver
   * "Onde a Plataforma Mente" D8. A guarda por `!changes` preserva edição
   * pendente ainda não salva, mesmo padrão de AppInclusion.Table.tsx.
   */
  useEffect(() => {
    if (!changes) {
      setRealRows(buildRows(people));
    }
  }, [people, changes]);

  const realColumns: GridColDef<(typeof realRows)[number]>[] = [
    // Common fields
    { 
      field: "name", 
      headerName: "Nome completo", 
      width: 350, 
      editable: editableFields.includes("name"),
      renderCell: (params) => <NameWithBadge name={params.value} maintained={params.row.maintainedByPj} />,
    },
    { 
      field: "taxPayerId", 
      headerName: "CPF beneficiário", 
      width: 150,
      editable: editableFields.includes("taxPayerId")
    },
    { 
      field: "taxPayerIdEmployee", 
      headerName: "CPF titular", 
      width: 150,
      editable: editableFields.includes("taxPayerIdEmployee")
    },
    { 
      field: "birthDate", 
      headerName: "Data de nascimento", 
      width: 150,
      editable: editableFields.includes("birthDate"),
      type: "date"
    },
    { 
      field: "motherName", 
      headerName: "Nome da mãe", 
      width: 200, 
      editable: editableFields.includes("motherName")
    },
    { 
      field: "weddingDate", 
      headerName: "Data de casamento", 
      width: 150, 
      editable: editableFields.includes("weddingDate"), 
      type: "date" 
    },
    { 
      field: "type", 
      headerName: "Tipo", 
      width: 120,
      editable: editableFields.includes("type")
    },
    
    // Employee specific fields
    { 
      field: "organizationId", 
      headerName: "ID Organização", 
      width: 150,
      editable: editableFields.includes("organizationId")
    },
    { 
      field: "cnpj", 
      headerName: "CNPJ empresa", 
      width: 150,
      editable: editableFields.includes("cnpj"),
      // Formata só a exibição: o valor da linha segue cru porque é comparado
      // com o dado original para detectar alteração e vai no payload.
      valueFormatter: (value: string) => formatCnpj(value),
      // O filtro compara com o valor cru: sem isto, buscar o que a tela
      // mostra (com máscara) não achava nada.
      filterOperators: cnpjFilterOperators,
    },
    { 
      field: "pis", 
      headerName: "PIS", 
      width: 130,
      editable: editableFields.includes("pis")
    },
    {
      field: "genderId",
      headerName: "Gênero",
      width: 150,
      editable: editableFields.includes("genderId"),
      type: "singleSelect",
      valueOptions: GENDER_OPTIONS,
      valueFormatter: (value: string) => GENDER_OPTIONS.find(o => o.value === value)?.label ?? value ?? "",
    },
    {
      field: "raceId",
      headerName: "Raça",
      width: 150,
      editable: editableFields.includes("raceId"),
      type: "singleSelect",
      valueOptions: RACE_OPTIONS,
      valueFormatter: (value: string) => RACE_OPTIONS.find(o => o.value === value)?.label ?? value ?? "",
    },
    {
      field: "maritalStatusId",
      headerName: "Estado Civil",
      width: 150,
      editable: editableFields.includes("maritalStatusId"),
      type: "singleSelect",
      valueOptions: MARITAL_STATUS_OPTIONS,
      valueFormatter: (value: string) => MARITAL_STATUS_OPTIONS.find(o => o.value === value)?.label ?? value ?? "",
    },
    {
      field: "educationLevelId",
      headerName: "Escolaridade",
      width: 220,
      editable: editableFields.includes("educationLevelId"),
      type: "singleSelect",
      valueOptions: EDUCATION_LEVEL_OPTIONS,
      valueFormatter: (value: string) => EDUCATION_LEVEL_OPTIONS.find(o => o.value === value)?.label ?? value ?? "",
    },
    { 
      field: "socialName", 
      headerName: "Nome Social", 
      width: 200,
      editable: editableFields.includes("socialName")
    },
    { 
      field: "birthCountryId", 
      headerName: "País Nascimento", 
      width: 130,
      editable: editableFields.includes("birthCountryId")
    },
    { 
      field: "nationalityCountryId", 
      headerName: "Nacionalidade", 
      width: 120,
      editable: editableFields.includes("nationalityCountryId")
    },
    { 
      field: "birthStateId", 
      headerName: "Estado Nascimento", 
      width: 130,
      editable: editableFields.includes("birthStateId")
    },
    { 
      field: "birthCityId", 
      headerName: "Cidade Nascimento", 
      width: 130,
      editable: editableFields.includes("birthCityId")
    },
    { 
      field: "fatherName", 
      headerName: "Nome do Pai", 
      width: 200,
      editable: editableFields.includes("fatherName")
    },
    { 
      field: "zipCode", 
      headerName: "CEP", 
      width: 100,
      editable: editableFields.includes("zipCode")
    },
    { 
      field: "addressType", 
      headerName: "Tipo Endereço", 
      width: 120,
      editable: editableFields.includes("addressType")
    },
    { 
      field: "address", 
      headerName: "Endereço", 
      width: 250,
      editable: editableFields.includes("address")
    },
    { 
      field: "addressNumber", 
      headerName: "Número", 
      width: 100,
      editable: editableFields.includes("addressNumber")
    },
    { 
      field: "complement", 
      headerName: "Complemento", 
      width: 150,
      editable: editableFields.includes("complement")
    },
    { 
      field: "neighborhood", 
      headerName: "Bairro", 
      width: 150,
      editable: editableFields.includes("neighborhood")
    },
    { 
      field: "stateId", 
      headerName: "Estado", 
      width: 100,
      editable: editableFields.includes("stateId")
    },
    { 
      field: "cityId", 
      headerName: "Cidade", 
      width: 130,
      editable: editableFields.includes("cityId")
    },
    { 
      field: "nationalIdentityNumber", 
      headerName: "RG", 
      width: 130,
      editable: editableFields.includes("nationalIdentityNumber")
    },
    { 
      field: "nationalIdentityEmissor", 
      headerName: "Órgão Emissor RG", 
      width: 130,
      editable: editableFields.includes("nationalIdentityEmissor")
    },
    { 
      field: "nationalIdentityExpeditionDate", 
      headerName: "Data Expedição RG", 
      width: 150,
      editable: editableFields.includes("nationalIdentityExpeditionDate"),
      type: "date"
    },
    { 
      field: "phoneNumber", 
      headerName: "Telefone", 
      width: 130,
      editable: editableFields.includes("phoneNumber")
    },
    { 
      field: "email", 
      headerName: "E-mail", 
      width: 200,
      editable: editableFields.includes("email")
    },
    { 
      field: "hiringDate", 
      headerName: "Data de admissão", 
      width: 150,
      editable: editableFields.includes("hiringDate"),
      type: "date"
    },
    
    // New fields from IMassaTotal
    { 
      field: "status", 
      headerName: "Status", 
      width: 120,
      editable: editableFields.includes("status")
    },
    { 
      field: "jobTitle", 
      headerName: "Posição", 
      width: 180,
      editable: editableFields.includes("jobTitle")
    },
    { 
      field: "paymentFrequency", 
      headerName: "Periodicidade Pagamento", 
      width: 180,
      editable: editableFields.includes("paymentFrequency")
    },
    // { 
    //   field: "grossPayment", 
    //   headerName: "Pagamento Bruto", 
    //   width: 150,
    //   editable: editableFields.includes("grossPayment")
    // },
    { 
      field: "municipality", 
      headerName: "Município", 
      width: 150,
      editable: editableFields.includes("municipality")
    },
    { 
      field: "cbo", 
      headerName: "CBO", 
      width: 100,
      editable: editableFields.includes("cbo")
    },
    {
      field: "dismissalDate",
      headerName: "Data Demissão",
      width: 150,
      editable: editableFields.includes("dismissalDate"),
      type: "date"
    },
    {
      field: "startPriorNoticeDate",
      headerName: "Início Aviso Prévio",
      width: 170,
      editable: editableFields.includes("startPriorNoticeDate"),
      type: "date"
    },
    {
      field: "endPriorNoticeDate",
      headerName: "Término Aviso Prévio",
      width: 180,
      editable: editableFields.includes("endPriorNoticeDate"),
      type: "date"
    },
    {
      field: "transferred",
      headerName: "Transferido",
      width: 120,
      editable: false
    },
    { 
      field: "plan", 
      headerName: "Plano", 
      width: 150,
      editable: false
    },
    
    // Dependent specific fields
    { 
      field: "employeeId", 
      headerName: "ID Funcionário", 
      width: 130,
      editable: editableFields.includes("employeeId")
    },
    {
      field: "dependentTypeId",
      headerName: "Tipo Dependente",
      width: 220,
      editable: editableFields.includes("dependentTypeId"),
      type: "singleSelect",
      valueOptions: DEPENDENT_TYPE_OPTIONS,
      valueFormatter: (value: string) => DEPENDENT_TYPE_OPTIONS.find(o => o.value === value)?.label ?? value ?? "",
    },
    { 
      field: "isStudent", 
      headerName: "Estudante", 
      width: 100,
      editable: editableFields.includes("isStudent")
    },
  ];

  async function handleSend() {
    try {
      setIsSendingUpdate(true);
      if (!changes) throw new Error("Nenhuma mudança identificada.");

      const payload = aplicarParticao(buildEditDataPayload(changes, people), partição);

      const res = await api.post(`/v2/apps/edit-data/${workspaceActivityId}`, {
        payload: payload,
      });

      // O polling do AppEditData.View.tsx eventualmente traria o dado novo, mas
      // invalidar aqui evita até 5s de janela onde a grid ainda mostra o valor
      // antigo logo depois de um save bem-sucedido.
      await queryClient.invalidateQueries({ queryKey: ["app_edit_data", workspaceActivityId] });

      setChanges(null);
      setIsModalOpen(false);
      if (setCurrentView) setCurrentView('DEFAULT');
      if (onSalvo) onSalvo(res?.data?.data);
      else openAlert('Dados salvos com sucesso.', 'success');
    } catch (error: any) {
      // Antes: catch vazio + window.location.reload() no finally, então uma
      // falha de rede ou 4xx/5xx era indistinguível de sucesso para o operador.
      // Ver "Onde a Plataforma Mente" D8.
      // Mesmo padrão de TemplateMovementsExport.View: a mensagem do backend
      // vem antes do genérico do axios ("Request failed with status code 500").
      const backendMessage = error?.response?.data?.data?.message;
      openAlert(backendMessage ?? error?.message ?? 'Erro ao salvar dados.', 'error');
    } finally {
      setIsSendingUpdate(false);
    }
  }

  function handleUndo() {
    setRealRows(buildRows(people));
    setChanges(null);
  }

  return (
    <>
      <Modal open={isModalOpen} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
        <Box sx={{ position: "absolute" as "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", height: "auto", width: "100%", maxWidth: 1000, bgcolor: "background.paper", boxShadow: 24, borderRadius: "1rem", border: "none", outline: "none", padding: "24px" }}>
          <div style={{"display":"flex","justifyContent":"space-between","alignItems":"center"}}>
            <h6 style={{"padding":"0","margin":"0","fontSize":"1.25rem","lineHeight":"1.75rem","fontWeight":600}}>Salvar alterações?</h6>
            <Close style={{"cursor":"pointer"}} onClick={() => setIsModalOpen(false)}/>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '14px', marginTop: '24px' }}>
            <span style={{ fontSize: '16px', lineHeight: '175%', fontWeight: '400', color: '#191C1A' }}>Por favor, confira os dados antes de confirmar. As informações anteriores serão substituídas.</span>
          </div>
          <TableContainer sx={{ "& .MuiTableCell-root": { background: "#FFFFFF !important" } }}>
            <Table>
              <TableRow style={{ height: "70px" }}>
                <TableCell>
                  <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "rgb(111, 111, 120)", fontWeight: "400"}}>Nome completo</span>
                </TableCell>
                <TableCell>
                  <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "rgb(111, 111, 120)", fontWeight: "400" }}>CPF beneficiário</span>
                </TableCell>
                <TableCell>
                  <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "rgb(111, 111, 120)", fontWeight: "400" }}>Campos editados</span>
                </TableCell>
              </TableRow>
              {changes && changes.map((row, index) => {
                console.log('Row data:', row);
                return (
                  <TableRow key={row.id || index} style={{ height: "70px" }}>
                    <TableCell>{typeof row.name === 'string' ? row.name : (row.name?.newer || row.name?.old || '-')}</TableCell>
                    <TableCell>{typeof row.taxPayerId === 'string' ? row.taxPayerId : (row.taxPayerId?.newer || row.taxPayerId?.old || '-')}</TableCell>
                    <TableCell>
                      {Object.keys(row)
                        .filter(key => !['id'].includes(key) && row[key]?.field)
                        .map(key => row[key]?.field)
                        .join(", ")}
                    </TableCell>
                  </TableRow>
                );
              })}
            </Table>
          </TableContainer>
          <div style={{"display":"flex","paddingTop":"0.5rem","paddingBottom":"0.5rem","marginTop":"1rem","justifyContent":"flex-end","alignItems":"center","width":"100%"}}>
            <div style={{"display":"flex","gap":"0.25rem"}}>
              <Button
                variant='outlined'
                color='error'
                style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
                onClick={() => setIsModalOpen(false)}
                disabled={isSendingUpdate}
              >
                Cancelar
              </Button>
  
              <Button
                variant='contained'
                style={{ minWidth: "180px", "paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
                onClick={handleSend}
                disabled={isSendingUpdate}
              >
                {!isSendingUpdate ? "Confirmar e Salvar" : <CircularProgress size="20px"/>}
              </Button>
            </div>
          </div>
        </Box>
      </Modal>

      {setCurrentView && (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <IconButton>
            <ArrowBack color="primary" style={{ width: "25px" }} onClick={() => setCurrentView('DEFAULT')}/>
          </IconButton>
          <h1 style={{"padding":"0","marginTop":"12px","fontSize":"1.25rem","lineHeight":"1.75rem","fontWeight":600}}>Editar dados da massa total</h1>
        </div>
      )}

      <DataGrid
        rows={realRows}
        columns={realColumns}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 10,
            },
          },
          ...initialCpfFilter(search),
        }}
        pageSizeOptions={[5, 10, 20, 50]}
        disableRowSelectionOnClick
        style={{ backgroundColor: "white", minHeight: 400 }}
        editMode="row"
        disableColumnFilter={false}
        disableColumnSelector={false}
        disableDensitySelector={false}
        getCellClassName={(params) => {
          const isEditable = editableFields.includes(params.field);
          return isEditable ? '' : 'non-editable-cell';
        }}
        sx={{
          '& .non-editable-cell': {
            color: '#9e9e9e',
            cursor: 'not-allowed',
            '&:hover': {
              backgroundColor: '#f0f0f0',
            },
          },
          '& .non-editable-cell .MuiDataGrid-cell': {
            fontStyle: 'italic',
          },
        }}
        //@ts-ignore
        processRowUpdate={(updatedRow, originalRow) => {
          const newArr = [...realRows];
          const idx = newArr.findIndex(r => r.id === updatedRow.id);
          newArr[idx] = { ...updatedRow };
          
          const originalData = people.find(row => 
            row?.TAX_PAYER_ID?.replace(/\D/g, "") === updatedRow?.taxPayerId?.replace(/\D/g, "")
          );
          
          if (!originalData) {
            setRealRows(newArr);
            return updatedRow;
          }

          const rowChanges: any = {
            id: updatedRow.id,
            name: updatedRow.name || '',
            taxPayerId: updatedRow.taxPayerId || '',
            type: updatedRow.type || ''
          };

          let hasChanges = false;

          const isDifferent = (newVal: any, oldVal: any, emptyValues = ['-', '', null, undefined]) => {
            if (newVal instanceof Date && oldVal instanceof Date) {
              return newVal.getTime() !== oldVal.getTime();
            }
            if (newVal instanceof Date && oldVal) {
              return newVal.getTime() !== new Date(oldVal).getTime();
            }
            if (oldVal instanceof Date && newVal) {
              return new Date(newVal).getTime() !== oldVal.getTime();
            }
            
            if (emptyValues.includes(newVal) && emptyValues.includes(oldVal)) return false;
            return newVal !== oldVal;
          };

          const fieldChecks = [
            // Common fields - mapping to IMassaTotal structure
            { field: 'name', label: 'Nome completo', newVal: updatedRow.name, oldVal: originalData.NAME },
            { field: 'taxPayerId', label: 'CPF beneficiário', newVal: updatedRow.taxPayerId, oldVal: originalData.TAX_PAYER_ID ? cpfMask(originalData.TAX_PAYER_ID) : '' },
            { field: 'taxPayerIdEmployee', label: 'CPF titular', newVal: updatedRow.taxPayerIdEmployee, oldVal: originalData.EMPLOYEE_TAX_PAYER_ID ? cpfMask(originalData.EMPLOYEE_TAX_PAYER_ID) : '' },
            { field: 'birthDate', label: 'Data de nascimento', newVal: updatedRow.birthDate, oldVal: originalData.BIRTH_DATE },
            { field: 'motherName', label: 'Nome da mãe', newVal: updatedRow.motherName, oldVal: originalData.MOTHER_NAME },
            { field: 'weddingDate', label: 'Data de casamento', newVal: updatedRow.weddingDate, oldVal: originalData.WEDDING_DATE },
            { field: 'type', label: 'Tipo', newVal: updatedRow.type, oldVal: originalData.PERSON_TYPE },
            
            // Employee specific fields - mapping to IMassaTotal structure
            { field: 'organizationId', label: 'ID Organização', newVal: updatedRow.organizationId, oldVal: originalData.ORGANIZATION_ID },
            { field: 'cnpj', label: 'CNPJ empresa', newVal: updatedRow.cnpj, oldVal: originalData.LEGAL_ENTITY_TAX_ID },
            { field: 'pis', label: 'PIS', newVal: updatedRow.pis, oldVal: originalData.PIS },
            { field: 'genderId', label: 'Gênero', newVal: updatedRow.genderId, oldVal: originalData.GENDER_ID },
            { field: 'raceId', label: 'Raça', newVal: updatedRow.raceId, oldVal: originalData.RACE_ID },
            { field: 'maritalStatusId', label: 'Estado Civil', newVal: updatedRow.maritalStatusId, oldVal: originalData.MARITAL_STATUS_ID },
            { field: 'educationLevelId', label: 'Escolaridade', newVal: updatedRow.educationLevelId, oldVal: originalData.EDUCATION_LEVEL_ID },
            { field: 'socialName', label: 'Nome Social', newVal: updatedRow.socialName, oldVal: originalData.SOCIAL_NAME },
            { field: 'birthCountryId', label: 'País Nascimento', newVal: updatedRow.birthCountryId, oldVal: originalData.BIRTH_COUNTRY_ID },
            { field: 'nationalityCountryId', label: 'Nacionalidade', newVal: updatedRow.nationalityCountryId, oldVal: originalData.NATIONALITY_COUNTRY_ID },
            { field: 'birthStateId', label: 'Estado Nascimento', newVal: updatedRow.birthStateId, oldVal: originalData.BIRTH_STATE_ID },
            { field: 'birthCityId', label: 'Cidade Nascimento', newVal: updatedRow.birthCityId, oldVal: originalData.BIRTH_CITY_ID },
            { field: 'fatherName', label: 'Nome do Pai', newVal: updatedRow.fatherName, oldVal: originalData.FATHER_NAME },
            { field: 'zipCode', label: 'CEP', newVal: updatedRow.zipCode, oldVal: originalData.ZIP_CODE },
            { field: 'addressType', label: 'Tipo Endereço', newVal: updatedRow.addressType, oldVal: originalData.ADDRESS_TYPE },
            { field: 'address', label: 'Endereço', newVal: updatedRow.address, oldVal: originalData.ADDRESS },
            { field: 'addressNumber', label: 'Número', newVal: updatedRow.addressNumber, oldVal: originalData.ADDRESS_NUMBER },
            { field: 'complement', label: 'Complemento', newVal: updatedRow.complement, oldVal: originalData.COMPLEMENT },
            { field: 'neighborhood', label: 'Bairro', newVal: updatedRow.neighborhood, oldVal: originalData.NEIGHBORHOOD },
            { field: 'stateId', label: 'Estado', newVal: updatedRow.stateId, oldVal: originalData.STATE_ID },
            { field: 'cityId', label: 'Cidade', newVal: updatedRow.cityId, oldVal: originalData.CITY_ID },
            { field: 'nationalIdentityNumber', label: 'RG', newVal: updatedRow.nationalIdentityNumber, oldVal: originalData.NATIONAL_IDENTITY_NUMBER },
            { field: 'nationalIdentityEmissor', label: 'Órgão Emissor RG', newVal: updatedRow.nationalIdentityEmissor, oldVal: originalData.NATIONAL_IDENTITY_EMISSOR },
            { field: 'nationalIdentityExpeditionDate', label: 'Data Expedição RG', newVal: updatedRow.nationalIdentityExpeditionDate, oldVal: originalData.NATIONAL_IDENTITY_EXPEDITION_DATE },
            { field: 'phoneNumber', label: 'Telefone', newVal: updatedRow.phoneNumber, oldVal: originalData.PHONE_NUMBER },
            { field: 'email', label: 'E-mail', newVal: updatedRow.email, oldVal: originalData.EMAIL },
            { field: 'hiringDate', label: 'Data de admissão', newVal: updatedRow.hiringDate, oldVal: originalData.HIRING_DATE },
            
            // New fields from IMassaTotal
            { field: 'status', label: 'Status', newVal: updatedRow.status, oldVal: originalData.STATUS },
            { field: 'jobTitle', label: 'Cargo', newVal: updatedRow.jobTitle, oldVal: originalData.JOB_TITLE },
            { field: 'paymentFrequency', label: 'Periodicidade Pagamento', newVal: updatedRow.paymentFrequency, oldVal: originalData.PAYMENT_FREQUENCY },
            { field: 'grossPayment', label: 'Pagamento Bruto', newVal: updatedRow.grossPayment, oldVal: originalData.GROSS_PAYMENT },
            { field: 'municipality', label: 'Município', newVal: updatedRow.municipality, oldVal: originalData.MUNICIPALITY },
            { field: 'cbo', label: 'CBO', newVal: updatedRow.cbo, oldVal: originalData.CBO },
            { field: 'dismissalDate', label: 'Data Demissão', newVal: updatedRow.dismissalDate, oldVal: parseDateOnly(originalData.DISMISSAL_DATE) },
            { field: 'startPriorNoticeDate', label: 'Início Aviso Prévio', newVal: updatedRow.startPriorNoticeDate, oldVal: originalData.START_PRIOR_NOTICE_DATE },
            { field: 'endPriorNoticeDate', label: 'Término Aviso Prévio', newVal: updatedRow.endPriorNoticeDate, oldVal: originalData.END_PRIOR_NOTICE_DATE },
            { field: 'transferred', label: 'Transferido', newVal: updatedRow.transferred, oldVal: originalData.TRANSFERRED },
            { field: 'plan', label: 'Plano', newVal: updatedRow.plan, oldVal: originalData.PLAN },
            
            // Dependent specific fields
            { field: 'employeeId', label: 'ID Funcionário', newVal: updatedRow.employeeId, oldVal: originalData.EMPLOYEE_ID },
            { field: 'dependentTypeId', label: 'Tipo Dependente', newVal: updatedRow.dependentTypeId, oldVal: originalData.DEPENDENT_TYPE_ID },
            { field: 'isStudent', label: 'Estudante', newVal: updatedRow.isStudent, oldVal: originalData.IS_STUDENT !== null ? (originalData.IS_STUDENT ? 'Sim' : 'Não') : '-' },
          ];

          fieldChecks.forEach(({ field, label, newVal, oldVal }) => {
            if (isDifferent(newVal, oldVal)) {
              rowChanges[field] = { old: oldVal, newer: newVal, field: label };
              hasChanges = true;
            }
          });

          if (hasChanges) {
            setChanges(prevChanges => {
              const existingChanges = prevChanges || [];
              const existingIndex = existingChanges.findIndex(c => c.id === updatedRow.id);
              
              if (existingIndex >= 0) {
                const updatedChanges = [...existingChanges];
                updatedChanges[existingIndex] = rowChanges;
                return updatedChanges;
              } else {
                return [...existingChanges, rowChanges];
              }
            });
          } else {
            setChanges(prevChanges => {
              if (!prevChanges) return null;
              const filtered = prevChanges.filter(c => c.id !== updatedRow.id);
              return filtered.length > 0 ? filtered : null;
            });
          }

          setRealRows(newArr);
          return updatedRow;
        }}
        onProcessRowUpdateError={() => {}}
        localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
      />

      <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'end', gap: "10px","paddingTop":"0.5rem","paddingBottom":"0.5rem","marginTop":"24px" }}>
        <Button
          variant='outlined'
          style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
          onClick={() => handleUndo()}
          disabled={!changes}
        >
          Descartar alterações
        </Button>
        <Button
          variant='contained'
          style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
          onClick={() => setIsModalOpen(true)}
          disabled={!changes}
        >
          Salvar
        </Button>
      </div>
    </>
  )
}
